import { useMemo, useState } from 'react'
import type { ChartResult, ChartSpec } from '../../../types/chart.js'
import { useNls } from '../../../nls/nlsProvider.js'
import './chart.css'

export interface TableChartProps {
  spec: ChartSpec
  data: ChartResult
  className?: string
  testId?: string
}

interface TableRow {
  key: string
  label: string
  values: Record<string, number | null>
}

export function TableChart({
  spec,
  data,
  className = '',
  testId,
}: TableChartProps) {
  const { t } = useNls()
  const [sortCol, setSortCol] = useState<string>('label')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const dimensionLabel = spec.breakdown?.field || spec.time?.field || 'Item'

  const columns = useMemo(() => {
    return data.series.map((s, idx) => ({
      key: s.key || `s_${idx}`,
      label: s.label || `Series ${idx + 1}`,
    }))
  }, [data.series])

  const rows = useMemo(() => {
    const rowMap = new Map<string, TableRow>()

    data.series.forEach((series, sIdx) => {
      const colKey = series.key || `s_${sIdx}`
      series.points.forEach((pt) => {
        const xKey = String(pt.x)
        let row = rowMap.get(xKey)
        if (!row) {
          row = {
            key: xKey,
            label: xKey,
            values: {},
          }
          rowMap.set(xKey, row)
        }
        row.values[colKey] = pt.y
      })
    })

    return Array.from(rowMap.values())
  }, [data.series])

  const sortedRows = useMemo(() => {
    const sorted = [...rows]
    sorted.sort((a, b) => {
      if (sortCol === 'label') {
        const cmp = a.label.localeCompare(b.label)
        return sortDir === 'asc' ? cmp : -cmp
      }
      const valA = a.values[sortCol] ?? 0
      const valB = b.values[sortCol] ?? 0
      const cmp = valA - valB
      return sortDir === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [rows, sortCol, sortDir])

  const handleHeaderClick = (colKey: string) => {
    if (sortCol === colKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortCol(colKey)
      setSortDir('asc')
    }
  }

  // CC-P11: 50 rows on screen
  const visibleRows = sortedRows.slice(0, 50)
  const isTruncated = sortedRows.length > 50

  return (
    <div className={`chartTableContainer ${className}`} data-testid={testId}>
      <table className="chartTable" data-testid="chart-table-element">
        <thead>
          <tr>
            <th
              scope="col"
              onClick={() => handleHeaderClick('label')}
              style={{ cursor: 'pointer' }}
              aria-sort={sortCol === 'label' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              {dimensionLabel} {sortCol === 'label' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
            </th>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                onClick={() => handleHeaderClick(col.key)}
                style={{ cursor: 'pointer' }}
                aria-sort={sortCol === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                {col.label} {sortCol === col.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) => (
            <tr key={row.key}>
              <td>{row.label}</td>
              {columns.map((col) => {
                const val = row.values[col.key]
                return (
                  <td key={col.key} style={{ textAlign: 'right' }}>
                    {val !== null && val !== undefined ? val.toLocaleString() : '—'}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {isTruncated && (
        <div className="chartTableFootnote" data-testid="chart-table-footnote">
          {t('NLSChartTableShowMore')} ({visibleRows.length} / {sortedRows.length})
        </div>
      )}
    </div>
  )
}
